<script lang="ts" setup>
import type { components } from '~/types/napis'

const route = useRoute()
const params = route.params
const gradId = params.id as string

if (!gradId) {
  throw createError({
    statusCode: 404,
    message: 'Certificate not found',
    fatal: true
  })
}

const { getGradProof } = useGradProof()

const gradProofData = await getGradProof(gradId, true)

if (!gradProofData) {
  throw createError({
    statusCode: 404,
    message: 'Certificate not found',
    fatal: true
  })
}

const student = gradProofData.student_part
const course = gradProofData.course as components['schemas']['CourseResponseSchema']
const certificate =
  gradProofData.certificate as components['schemas']['CourseCertificateResponseSchema']
const gradImage = gradProofData.file_part?.image_url
const gradPdf = gradProofData.file_part?.pdf_url

const gradTitle = computed(() => {
  if (
    course &&
    course.contentful_content &&
    typeof course.contentful_content === 'object' &&
    'title' in course.contentful_content
  ) {
    return course.contentful_content.title as string
  }
  if (
    certificate &&
    certificate.contentful_content &&
    typeof certificate.contentful_content === 'object' &&
    'title' in certificate.contentful_content
  ) {
    return certificate.contentful_content.title as string
  }
  return 'Certificate not found'
})

const chapters = computed(() => {
  if (course) {
    // Filter out important chapters that are projects
    return (course?.chapters ?? []).filter(
      (c) =>
        c.contentful_content &&
        typeof c.contentful_content === 'object' &&
        'title' in c.contentful_content &&
        typeof c.contentful_content.title === 'string' &&
        (c.contentful_content.title as string).startsWith('Project') !== true &&
        (c.contentful_content.title as string).startsWith('0') !== true
    )
  }
  return []
})

const courses = computed(() => {
  if (certificate) {
    return certificate.courses
  }
  return []
})

const projects: any = computed(() => {
  const _projects = []
  if (course && course.chapters) {
    for (const chapter of course.chapters) {
      if (chapter.materials) {
        for (const material of chapter.materials) {
          if (material.material_type === 'PROJECT') {
            _projects.push(material)
          }
        }
      }
    }
  } else if (certificate) {
    for (const _course of courses.value) {
      if (_course.chapters) {
        for (const chapter of _course.chapters) {
          if (chapter.materials) {
            for (const material of chapter.materials) {
              if (material.material_type === 'PROJECT') {
                _projects.push(material)
              }
            }
          }
        }
      }
    }
  }
  return _projects
})

// config
const runtimeConfig = useRuntimeConfig()
const siteUrl = runtimeConfig.public.siteUrl
const pageUrl = `${siteUrl}${route.path}`
const ogTitle = `${gradTitle.value} Certificate - ${student.first_name} ${student.last_name}`
const ogDescription =
  'This certificate was awarded upon successful completion of TalentLabs ' +
  gradTitle.value +
  ' and can be verified on TalentLabs platform with this link.'

useSeoMeta({
  title: () => ogTitle,
  description: () => ogDescription,
  ogSiteName: () => ogTitle,
  ogTitle: () => ogTitle,
  ogDescription: () => ogDescription,
  ogUrl: () => pageUrl,
  ogImageUrl: () => gradImage,
  ogImageHeight: 630,
  ogImageWidth: 1200,
  ogImageAlt: () => ogTitle,
  ogImageType: 'image/png',
  ogImageSecureUrl: () => gradImage,
  twitterCard: 'summary_large_image',
  twitterTitle: ogTitle,
  twitterDescription: () => ogDescription,
  twitterImage: () => gradImage
})

definePageMeta({
  layout: 'student'
})

// date
const getDate = () => {
  return formatFromISOString(gradProofData.graduation_date)
}

// Cert methods

const courseId = mapCourseTitleToCourseId(gradTitle.value)

const courseClickable = courseId && courseId !== 'case'

function goToProfile() {
  window.open(`/profile/${student.id_slug}`)
}

// Share page

const { sharePageLink } = useShare()

const shareCertificate = () => {
  const title = `Check out my ${gradTitle.value} from TalentLabs!`
  const linkTitle = `Share your TalentLabs certificate, ${gradTitle.value}`

  sharePageLink(pageUrl, 'TalentLabs Certificate', title, linkTitle)
}

// Get course badge

const { getCourseBadgeFileName } = useStaticData()
const badgeSlug = computed(() => getCourseBadgeFileName(gradTitle.value))
</script>

<template>
  <div id="certificate" class="container">
    <h1>{{ gradTitle }}</h1>

    <div id="certificate-content">
      <div id="certificate-details">
        <h4>Certificate awarded to</h4>

        <div id="certificate-details-student" class="clickable" @click="goToProfile">
          <img
            v-if="student.profile_image?.presigned_get_object_url"
            :src="student.profile_image.presigned_get_object_url"
            alt="name"
          >
          <div v-else id="certificate-details-student-avatar">
            <CommonIconAvatarDefault />
          </div>

          <div id="certificate-details-student-text">
            <p>
              <b>{{ student.first_name }} {{ student.last_name }}</b>
            </p>

            <span>Issued on: {{ getDate() }}</span>
          </div>

          <div class="certificate-chevron">
            <CommonIconChevron />
          </div>
        </div>

        <div id="certificate-details-course">
          <h4>The course</h4>

          <NuxtLink
            id="certificate-details-course-header"
            :class="{
              // Some courses are no longer available, we wont make them clickable
              clickable: courseClickable
            }"
            :to="courseClickable ? `/courses/${courseId}` : ''"
          >
            <img :src="`/images/database/courses/badges/${badgeSlug}`" :alt="gradTitle" >

            <div id="certificate-details-course-header-title">
              <h2>{{ gradTitle }}</h2>
              <h3>TalentLabs</h3>
            </div>

            <div v-if="courseClickable" class="certificate-chevron">
              <CommonIconChevron />
            </div>
          </NuxtLink>

          <h4 v-if="chapters.length > 0 || courses.length > 0">Topics covered</h4>

          <div v-if="chapters.length > 0" id="certificate-details-course-modules">
            <template v-for="chapter in chapters" :key="chapter.title">
              <PageCoursesCourseCheckpoint v-if="chapter.title" :title="chapter.title" />
            </template>
          </div>

          <div v-if="courses.length > 0" id="certificate-details-course-modules">
            <template v-for="c in courses" :key="c.id_slug">
              <PageCoursesCourseCheckpoint
                v-if="
                  c.contentful_content &&
                  typeof c.contentful_content === 'object' &&
                  'title' in c.contentful_content
                "
                :title="
                  c.contentful_content &&
                  typeof c.contentful_content === 'object' &&
                  'title' in c.contentful_content
                    ? (c.contentful_content.title as string)
                    : ''
                "
              />
            </template>
          </div>

          <h4 v-if="projects.length > 0">Hands-on-projects</h4>

          <div v-if="projects.length > 0">
            <PageCoursesCourseCheckpoint
              v-for="project in projects"
              :key="project.id_slug"
              :title="project.contentful_content_part.title || ''"
            />
          </div>
        </div>
      </div>

      <div v-if="gradImage && gradPdf" id="certificate-img">
        <img :src="gradImage" :alt="`${gradTitle} certificate`" >

        <div id="certificate-img-btns">
          <CommonButton @handle-click="shareCertificate">
            Share
            <CommonIconShare />
          </CommonButton>

          <CommonButton black :href="gradPdf" download="Certificate.pdf">
            Download
            <CommonIconDownload />
          </CommonButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
#certificate {
  padding: 40px 0 80px;

  h4,
  h1 {
    display: block;
    position: relative;
  }

  h1 {
    width: 50%;
    line-height: 2.5rem;
  }

  h4 {
    font-size: 0.95rem;
    margin: 50px 0 15px;
    font-weight: 500;
    color: var(--textLight);
  }

  h1::before {
    position: absolute;
    background-color: var(--purple);
    content: '';
    left: 0;
  }

  h1::before {
    height: 5px;
    width: 70px;
    bottom: -7px;
  }

  h4:first-of-type {
    margin-top: 0;
  }

  &-content {
    display: grid;
    grid-template-columns: 1fr 40%;
    gap: 200px;
    margin-top: 45px;
    position: relative;
    align-items: flex-start;
  }

  &-img {
    position: sticky;
    top: 140px;

    img {
      width: 100%;
    }

    &-btns {
      margin-top: 20px;
      display: flex;
      gap: 10px;

      :deep(.btn-m) {
        display: flex;
        align-items: center;
        gap: 10px;
        padding-left: 32px;

        svg {
          $iconSize: 14px;
          height: $iconSize;
          width: $iconSize;
        }
      }
    }
  }

  &-details {
    $chevronSize: 15px;

    .certificate-chevron {
      svg {
        height: $chevronSize;
        width: $chevronSize;
        fill: var(--textLight);
      }
    }

    &-student {
      $imgSize: 80px;

      display: grid;
      grid-template-columns: $imgSize 1fr $chevronSize;
      gap: 25px;
      align-items: center;
      border: 1px solid gainsboro;
      padding: 20px;
      border-radius: 15px;

      img {
        height: $imgSize;
        width: $imgSize;
        object-fit: cover;
        border-radius: 50%;
        border: 1px solid rgb(210, 210, 210);
      }

      p {
        font-size: 1.3rem;
        line-height: 1.8rem;
        margin-bottom: 5px;
      }

      span {
        color: var(--textMedium);
        font-size: 0.95rem;
      }
    }

    &-course {
      margin-top: 40px;

      &-header {
        $badgeSize: 70px;

        display: grid;
        grid-template-columns: $badgeSize 1fr $chevronSize;
        align-items: center;
        gap: 25px;
        padding: 15px 20px 15px 15px;
        border: 1px solid gainsboro;
        border-radius: 15px;

        img {
          height: $badgeSize;
          width: $badgeSize;
          background-color: var(--bgMedium);
          border-radius: 15px;
        }

        &-title {
          h2 {
            font-size: 1.1rem;
            line-height: 1.6rem;
            font-weight: 500;
            margin-bottom: 5px;
          }

          h3 {
            color: var(--textMedium);
            font-weight: 400;
            font-size: 0.95rem;
          }
        }
      }

      &-modules {
        columns: 2;
        -webkit-columns: 2;
        -moz-columns: 2;
        column-gap: 40px;
      }

      :deep(.check-point) {
        margin: 0 0 10px;
        line-height: 1.5rem;
      }
    }
  }
}

@media (max-width: 1300px) {
  #certificate {
    &-content {
      grid-template-columns: 1fr 40%;
      gap: 100px;
    }
  }
}

@media (max-width: 1150px) {
  #certificate {
    width: 700px;
    margin: auto;

    &-content {
      display: flex;
      flex-direction: column-reverse;
      gap: 60px;
    }

    &-img {
      position: relative;
      top: 0;
    }
  }
}

@media (max-width: 900px) {
  #certificate {
    :deep(.popup-screen-container) {
      width: 100dvw;
      padding-bottom: 20px;
    }
  }
}

@media (max-width: 800px) {
  #certificate {
    width: auto;
    margin: 0 20px;
    padding-top: 20px;

    h1 {
      width: 80%;
    }

    &-img {
      &-btns {
        display: grid;
        grid-template-columns: 1fr 1fr;
      }
    }
  }
}

@media (max-width: 600px) {
  #certificate {
    padding-top: 30px;

    h1 {
      font-size: 1.5rem;
      line-height: 2rem;
    }

    &-content {
      gap: 45px;
    }

    &-img {
      &-btns {
        margin: 17px 0 5px;
        display: grid;
        grid-template-columns: 1fr;
        gap: 7px;
      }
    }

    &-details {
      width: 100%;

      &-student {
        display: block;
        position: relative;

        img {
          $imgSize: 60px;
          height: $imgSize;
          width: $imgSize;
        }

        p {
          font-size: 1.2rem;
          line-height: 1.7rem;
          margin: 10px 40px 3px 0;
        }

        .certificate-chevron {
          position: absolute;
          top: 0;
          bottom: 0;
          right: 20px;
          display: flex;
          align-items: center;
        }
      }

      &-course {
        &-header {
          $badgeSize: 65px;
          $chevronSize: 15px;

          grid-template-columns: $badgeSize 1fr $chevronSize;
          gap: 20px;

          img {
            height: $badgeSize;
            width: $badgeSize;
          }

          &-title {
            h2 {
              font-size: 1rem;
              line-height: 1.5rem;
              margin-bottom: 3px;
            }

            h3 {
              font-size: 0.9rem;
            }
          }
        }

        &-modules {
          columns: 1;
          -webkit-columns: 1;
          -moz-columns: 1;
          padding-left: 2px;
          display: flex;
          flex-direction: column;
          gap: 12px;

          :deep(.check-point) {
            margin: 0;
          }
        }
      }
    }
  }
}

@media (max-width: 370px) {
  #certificate {
    &-details {
      &-course {
        &-header {
          display: block;
          padding: 20px;
          position: relative;
          padding-right: 50px;

          img {
            margin-bottom: 10px;
          }

          svg {
            position: absolute;
            top: 0;
            bottom: 0;
            right: 15px;
            margin: auto;
          }
        }
      }
    }
  }
}

@media (hover: hover) {
  #certificate {
    &-details {
      &-student:hover {
        border: 1px solid var(--purple);
      }

      &-course {
        &-header:hover {
          border: 1px solid var(--purple);
        }
      }
    }
  }
}
</style>
